import { useQuery } from "@tanstack/react-query";
import type { SellerCustomDomainView } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";

type UseCustomDomainQueryParams = {
  storeSlug: string;
};

// Stop auto-polling after ~10 minutes (120 × 5s) so a domain stuck in
// provisioning doesn't poll forever while the tab is open. The seller can
// still refresh manually.
const MAX_STATUS_POLLS = 120;

export function useCustomDomainQuery({ storeSlug }: UseCustomDomainQueryParams) {
  const query = useQuery({
    queryKey: queryKeys.seller.customDomain(storeSlug),
    // The backend returns `null` (empty 200 body) when no domain is set, which
    // apiJsonClient surfaces as `undefined`. React Query forbids a queryFn from
    // resolving `undefined`, so coerce it to `null`.
    queryFn: async () =>
      (await apiJsonClient<SellerCustomDomainView | null>(
        endpoints.seller.customDomains,
        { cache: "no-store" },
      )) ?? null,
    enabled: storeSlug.length > 0,
    // Poll only while VERIFYING (after the seller confirmed the DNS record).
    // A domain still awaiting confirmation (pending) isn't checked at all —
    // the seller triggers verification with the "I've added it" button.
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      if (status !== "provisioning") return false;
      return q.state.dataUpdateCount < MAX_STATUS_POLLS ? 5000 : false;
    },
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
